import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {SINGLE_DATASET_OUTPUT_STEP_DATA_NAME} from 'analysis-tool/AnalysisStepDataNames';
import {NoPreviewPlaceholder} from 'analysis-tool/ui/view-templates/StepConfigurationViewTemplate';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import PersistedDataFrameTableWrapper from 'data-frames/wrapper/PersistedDataFrameTableWrapper';
import {PipelineStepDataKind, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getPreviewOutputStepData, PreviewOutput, PreviewStepParams} from 'pipelines/network/PipelineDAO';
import {getOutputDataForView} from 'pipelines/PipelineViewUtils';
import {AsyncState} from 'ui-library/hooks/useAsyncState';

import {DATA_FRAME_CREATION_NO_PREVIEW_PLACEHOLDER_TEXT} from '../DataFrameCreationConstants';
import DataFrameTablePreview from '../DataFrameTablePreview';
import {CombineDataFramesState} from './CombineDataFramesState';

function InputTablePreview({dataFrameId, name}: {dataFrameId?: string; name: string}): JSX.Element {
  if (dataFrameId == null) {
    return <NoPreviewPlaceholder title={`Select the ${name} table to combine`} />;
  }

  return (
    <PersistedDataFrameTableWrapper
      actionOrigin={ActionOrigin.ANALYSIS_TOOL}
      dataFrameId={dataFrameId}
      clientStateId={`combine-tables-preview-${name}-${dataFrameId}`}
    />
  );
}

interface CombineDataFramesPreviewProps {
  clientStateId: string;
  state: CombineDataFramesState;
  previewState: AsyncState<
    PreviewOutput<PipelineStepType.JOIN>,
    [PreviewStepParams<PipelineStepType.JOIN> | PreviewStepParams<PipelineStepType.UNION_DATASETS>]
  >;
}

export default function CombineDataFramesPreview({
  clientStateId,
  state,
  previewState,
}: CombineDataFramesPreviewProps): JSX.Element {
  const leftDataFrameId =
    state.leftTable != null
      ? getOutputDataForView(state.leftTable, undefined, PipelineStepDataKind.DATASET).id
      : undefined;
  const rightDataFrameId =
    state.rightTable != null
      ? getOutputDataForView(state.rightTable, undefined, PipelineStepDataKind.DATASET).id
      : undefined;

  if (leftDataFrameId == null && rightDataFrameId == null) {
    return (
      <NoPreviewPlaceholder
        title={DATA_FRAME_CREATION_NO_PREVIEW_PLACEHOLDER_TEXT}
        subtitle="Select the tables to combine."
      />
    );
  }

  return (
    <div className={css(styles.stack)}>
      <div className={css(styles.tableCard)}>
        <div className={css(styles.table)}>
          <InputTablePreview dataFrameId={leftDataFrameId} name="left" />
        </div>
      </div>
      <div className={css(styles.tableCard)}>
        <div className={css(styles.table)}>
          <InputTablePreview dataFrameId={rightDataFrameId} name="right" />
        </div>
      </div>
      <div className={css(styles.tableCard)}>
        <div className={css(styles.table)}>
          <DataFrameTablePreview
            clientStateId={clientStateId}
            errorMessage="The tables could not be combined."
            getDataFrameFromOutput={(previewOutput: PreviewOutput<PipelineStepType.JOIN>) => {
              const dataFrame = getPreviewOutputStepData(
                previewOutput,
                SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
                PipelineStepDataKind.DATASET
              )[0];
              // Override the name with the current user-typed name from state
              return {...dataFrame, name: state.dataFrameName};
            }}
            noPreviewSubtitle="Finish configuring how to combine the left and right tables"
            noPreviewTitle="A preview of the combined table is not yet available"
            previewState={previewState}
          />
        </div>
      </div>
    </div>
  );
}

const TABLE_CARD_MARGIN = 12;

const styles = StyleSheet.create({
  stack: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'auto',
    paddingBottom: TABLE_CARD_MARGIN / 2,
    paddingTop: TABLE_CARD_MARGIN / 2,
  },
  table: {
    height: '100%',
  },
  tableCard: {
    borderRadius: 4,
    height: `calc(33% - ${(TABLE_CARD_MARGIN * 2) / 3}px)`,
    marginBottom: TABLE_CARD_MARGIN / 2,
    marginLeft: TABLE_CARD_MARGIN,
    marginRight: TABLE_CARD_MARGIN,
    marginTop: TABLE_CARD_MARGIN / 2,
  },
});
